// client/src/pages/Completed1040PaperSourceDocsReport.tsx
//
// Lists completed 1040 returns where the source-document settings are flagged
// PAPER. Each row can be marked reviewed; reviewed rows drop out of the table.
//
// Data: GET /api/portal-data?type=1040PaperSourceDocs  (pass-through to PE)
// Reviews: GET /api/audit-reviews?reportKey=1040-paper-source-docs
//          POST /api/audit-review  { reportKey, rowKey }

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RotateCcw, CheckSquare, Files, Search,
} from 'lucide-react';

// ─── Constants ───────────────────────────────────────────────────────────────

const REPORT_KEY     = '1040-paper-source-docs';
const REVIEW_POLL_MS = 30 * 1000;
const ACCENT         = '#fb923c'; // orange — paper / source docs theme

// ─── Types ───────────────────────────────────────────────────────────────────

type PaperSrcRow = {
  Job_Idx?:     number | string;
  ClientCode?:  string;
  ClientName?:  string;
  ClientOffice?: string;
  JobPartner?:  string;
  JobManager?:  string;
  Job_Name?:    string;
  WorkStatus?:  string;
  Question?:    string;
  Answer?:      string;
};

type ReviewRecord = {
  row_key:     string;
  reviewed_by: string;
  reviewed_at: string;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function norm(s: unknown): string {
  return (s ?? '').toString().toLowerCase().trim();
}

const GOOD_STATUSES = ['filed', 'accepted', 'complete', 'completed', 'done', 'approved', 'transmitted', 'efiled'];
const WARN_STATUSES = ['in progress', 'in review', 'pending', 'extension', 'on extension'];
const BAD_STATUSES  = ['overdue', 'error', 'rejected', 'missing'];

function statusClass(s: string | undefined): string {
  if (!s) return '';
  const low = s.toLowerCase();
  if (GOOD_STATUSES.some(g => low.includes(g))) return 'good';
  if (WARN_STATUSES.some(w => low.includes(w))) return 'warn';
  if (BAD_STATUSES.some(b  => low.includes(b))) return 'bad';
  return '';
}

// ─── Fetch ───────────────────────────────────────────────────────────────────

async function fetchRows(): Promise<PaperSrcRow[]> {
  const resp = await fetch('/api/portal-data?type=1040PaperSourceDocs', {
    method:      'GET',
    cache:       'no-store',
    credentials: 'include',
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Failed to load report (HTTP ${resp.status}). ${text}`);
  }
  const data = (await resp.json()) as unknown;
  return Array.isArray(data) ? (data as PaperSrcRow[]) : [];
}

async function fetchReviews(): Promise<ReviewRecord[]> {
  const resp = await fetch(`/api/audit-reviews?reportKey=${encodeURIComponent(REPORT_KEY)}`, {
    credentials: 'include',
    cache:       'no-store',
  });
  if (!resp.ok) return [];
  const data = await resp.json();
  return data.reviews || [];
}

async function postReview(rowKey: string): Promise<void> {
  const resp = await fetch('/api/audit-review', {
    method:      'POST',
    credentials: 'include',
    headers:     { 'content-type': 'application/json' },
    body:        JSON.stringify({ reportKey: REPORT_KEY, rowKey: String(rowKey) }),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Failed to mark reviewed (HTTP ${resp.status}). ${text}`);
  }
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function Completed1040PaperSourceDocsReport() {
  const navigate = useNavigate();

  const [rows, setRows]                 = useState<PaperSrcRow[]>([]);
  const [reviewedKeys, setReviewedKeys] = useState<Set<string>>(new Set());
  const [fetchedAt, setFetchedAt]       = useState<number>(0);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState<string | null>(null);
  const [search, setSearch]             = useState('');
  const [markingRow, setMarkingRow]     = useState<string | null>(null);

  const reviewIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadRows = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const data = await fetchRows();
      setRows(data);
      setFetchedAt(Date.now());
      setError(null);
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  const loadReviews = useCallback(async () => {
    try {
      const reviews = await fetchReviews();
      setReviewedKeys(new Set(reviews.map(r => String(r.row_key))));
    } catch {
      // best-effort polling — never error
    }
  }, []);

  useEffect(() => {
    Promise.all([loadRows(true), loadReviews()]);
    reviewIntervalRef.current = setInterval(loadReviews, REVIEW_POLL_MS);
    return () => {
      if (reviewIntervalRef.current) clearInterval(reviewIntervalRef.current);
    };
  }, [loadRows, loadReviews]);

  const handleMarkReviewed = useCallback(async (rowKey: string) => {
    setMarkingRow(rowKey);
    // Optimistic: hide immediately
    setReviewedKeys(prev => new Set([...prev, rowKey]));
    try {
      await postReview(rowKey);
    } catch {
      setReviewedKeys(prev => {
        const next = new Set(prev);
        next.delete(rowKey);
        return next;
      });
    } finally {
      setMarkingRow(null);
    }
  }, []);

  // ── Filter: exclude reviewed + search ──────────────────────────────────────

  const filtered = useMemo(() => {
    const q = norm(search);
    return rows.filter(r => {
      const key = String(r.Job_Idx ?? '');
      if (reviewedKeys.has(key)) return false;
      if (!q) return true;
      const haystack = [
        r.ClientCode, r.ClientName, r.ClientOffice, r.JobPartner, r.JobManager,
        r.Job_Name, r.WorkStatus, r.Question, r.Answer,
      ].map(norm).join(' ');
      return haystack.includes(q);
    });
  }, [rows, reviewedKeys, search]);

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn" onClick={() => navigate('/reports')} style={{ padding: '7px 9px' }}>
            <ArrowLeft size={15} />
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 20, fontWeight: 700, letterSpacing: '-.01em' }}>
              <Files size={18} style={{ color: ACCENT }} />
              Completed 1040s w/ Paper Source Docs
            </div>
            <div className="muted" style={{ fontSize: 13, marginTop: 3 }}>
              {fetchedAt > 0
                ? `${filtered.length.toLocaleString()} pending review${reviewedKeys.size > 0 ? ` · ${reviewedKeys.size.toLocaleString()} reviewed` : ''} · refreshed ${new Date(fetchedAt).toLocaleTimeString()}`
                : 'Loading…'}
            </div>
          </div>
        </div>
        <button className="btn" onClick={() => loadRows(true)} disabled={loading}>
          <RotateCcw size={14} style={loading ? { animation: 'spin 1s linear infinite' } : undefined} />
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(255,107,107,.1)', border: '1px solid rgba(255,107,107,.3)', color: '#ffd1d1', fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Search */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: 360 }}>
          <Search size={14} style={{
            position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
            color: 'var(--muted)', pointerEvents: 'none',
          }} />
          <input
            placeholder="Search client, partner, staff, question…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', paddingLeft: 30 }}
          />
        </div>
        {search && (
          <button className="btn" onClick={() => setSearch('')}>Clear</button>
        )}
      </div>

      {/* Table */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(0,0,0,.18)' }}>
                {[
                  'Code', 'Client', 'Office', 'Job Name', 'Partner', 'Manager',
                  'Work Status', 'Question', 'Answer',
                ].map(h => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left', padding: '9px 10px', fontSize: 12,
                      color: 'var(--muted)', whiteSpace: 'nowrap',
                    }}
                  >
                    {h}
                  </th>
                ))}
                <th style={{ textAlign: 'right', padding: '9px 10px', fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap', width: 150 }}>
                  Mark Reviewed
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)' }}>Loading…</td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)' }}>
                    {rows.length === 0
                      ? 'No 1040s with paper source docs found.'
                      : 'All matching rows have been reviewed.'}
                  </td>
                </tr>
              )}
              {filtered.map((row, i) => {
                const key      = String(row.Job_Idx ?? `${row.ClientCode}-${i}`);
                const isMarking = markingRow === key;
                return (
                  <tr
                    key={key}
                    style={{ borderBottom: '1px solid var(--border)', transition: 'background .1s' }}
                    onMouseEnter={e => (e.currentTarget.style.background = `${ACCENT}08`)}
                    onMouseLeave={e => (e.currentTarget.style.background = '')}
                  >
                    <td style={{ padding: '8px 10px', color: 'var(--muted)', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {row.ClientCode || '—'}
                    </td>
                    <td style={{ padding: '8px 10px', fontWeight: 600, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.ClientName || '—'}
                    </td>
                    <td style={{ padding: '8px 10px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                      {row.ClientOffice || '—'}
                    </td>
                    <td style={{ padding: '8px 10px', color: 'var(--muted)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.Job_Name || '—'}
                    </td>
                    <td style={{ padding: '8px 10px', color: 'var(--muted)', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.JobPartner || '—'}
                    </td>
                    <td style={{ padding: '8px 10px', color: 'var(--muted)', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.JobManager || '—'}
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      {row.WorkStatus
                        ? <span className={`pill ${statusClass(row.WorkStatus)}`}>{row.WorkStatus}</span>
                        : <span style={{ color: 'var(--muted)' }}>—</span>}
                    </td>
                    <td style={{ padding: '8px 10px', color: 'var(--muted)' }}>
                      {row.Question || '—'}
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      {row.Answer
                        ? (
                          <span style={{
                            display: 'inline-block', padding: '2px 8px', borderRadius: 999,
                            fontSize: 11, fontWeight: 700, letterSpacing: '.3px',
                            color: ACCENT, background: `${ACCENT}18`, border: `1px solid ${ACCENT}55`,
                          }}>
                            {row.Answer}
                          </span>
                        )
                        : <span style={{ color: 'var(--muted)' }}>—</span>}
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                      <button
                        onClick={() => handleMarkReviewed(key)}
                        disabled={isMarking}
                        title="Mark this row as reviewed (it will drop from the list)"
                        style={{
                          background: 'none',
                          border: '1px solid rgba(255,255,255,0.25)',
                          borderRadius: 6,
                          color: isMarking ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.7)',
                          cursor: isMarking ? 'wait' : 'pointer',
                          padding: '4px 10px',
                          fontSize: 12,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 5,
                          transition: 'all .15s',
                        }}
                        onMouseEnter={e => {
                          if (isMarking) return;
                          e.currentTarget.style.borderColor = '#57d38c';
                          e.currentTarget.style.color = '#57d38c';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)';
                          e.currentTarget.style.color = 'rgba(255,255,255,0.7)';
                        }}
                      >
                        <CheckSquare size={13} />
                        {isMarking ? 'Saving…' : 'Reviewed'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!loading && filtered.length > 0 && (
          <div style={{ padding: '8px 14px', borderTop: '1px solid var(--border)', fontSize: 12, color: 'var(--muted)', background: 'rgba(0,0,0,.1)' }}>
            Showing {filtered.length.toLocaleString()} of {rows.length.toLocaleString()} rows
            {reviewedKeys.size > 0 && ` · ${reviewedKeys.size.toLocaleString()} reviewed`}
          </div>
        )}
      </div>
    </div>
  );
}
